import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import LogoSidenavbar from "../../assets/dashboard/Logo-SideNavBar.svg";

import {
  MdiUsersGroupOutline,
  TablerClipboardData,
  TablerReportMoney,
  TablerBrowserCheck,
  TablerDiamond,
} from "../../assets/icons/LayoutIcon";
import { LayoutDashboard, FileText, FileChartLine, X } from "lucide-react";

const NAV_ITEMS = {
  dashboard: { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  sales: { path: "/sales", label: "Sales", icon: TablerClipboardData },
  applicationReport: {
    path: "/application-report",
    label: "Application Report",
    icon: FileText,
  },
  salesReport: {
    path: "/sales-report",
    label: "Sales Report",
    icon: TablerReportMoney,
  },
  purchaseOrderReport: {
    path: "/purchase-order-report",
    label: "Purchase Order Report",
    icon: FileChartLine,
  },
  manageUsers: {
    path: "/manage-users",
    label: "Manage Users",
    icon: MdiUsersGroupOutline,
  },
  productSalesReport: {
    path: "/product-sales-report",
    label: "Product Sales Report",
    icon: TablerClipboardData,
  },
  productLeadReport: {
    path: "/product-lead-report",
    label: "Product Lead Report",
    icon: TablerDiamond,
  },
  customerVisit: {
    path: "/customer-visit",
    label: "Customer Visit",
    icon: TablerBrowserCheck,
  },
  visit: {
    path: "/visit",
    label: "Visit",
    icon: TablerBrowserCheck,
  },
  workReport: {
    path: "/work-report",
    label: "Work Report",
    icon: TablerBrowserCheck,
  },
};

const ROLE_NAV_ITEMS = {
  super_admin: [
    "dashboard",
    "sales",
    "applicationReport",
    "salesReport",
    "purchaseOrderReport",
    "manageUsers",
  ],
  manager: [
    "dashboard",
    "sales",
    // "visit",
    "customerVisit",
    "applicationReport",
    "salesReport",
    "purchaseOrderReport",
  ],
  distributor_admin: [
    "dashboard",
    "productSalesReport",
    "productLeadReport",
    "applicationReport",
    "salesReport",
    "purchaseOrderReport",
  ],
  application_specialist: [
    "dashboard",
    "productSalesReport",
    "productLeadReport",
    "applicationReport",
    "salesReport",
    "customerVisit",
  ],
  regular_user: ["dashboard", "sales"],
};

const SideNavBar = ({ isSidebarVisible, onClose, userRole }) => {
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0 });
  const sidebarRef = useRef(null);
  const navItemsRef = useRef([]);
  const navigationItems =
    ROLE_NAV_ITEMS[userRole]?.map((key) => NAV_ITEMS[key]) || [];

  // Calculate tooltip position when hovering an item
  useEffect(() => {
    if (hoveredItem !== null && navItemsRef.current[hoveredItem]) {
      const itemRect = navItemsRef.current[hoveredItem].getBoundingClientRect();
      const sidebarRect = sidebarRef.current.getBoundingClientRect();

      // Calculate tooltip position based on the item's position
      setTooltipPosition({
        top: itemRect.top + itemRect.height / 2,
        left: sidebarRect.width + 5, // Position right after the sidebar
      });
    }
  }, [hoveredItem]);

  return (
    <>
      {isSidebarVisible && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 sm:hidden"
          onClick={onClose}
        />
      )}

      <div
        ref={sidebarRef}
        className={`fixed top-0 left-0 h-full z-50 bg-secondary transition-transform duration-300 md:relative md:block ${
          isSidebarVisible
            ? "translate-x-0 min-w-40 lg:min-w-64"
            : "-translate-x-full md:translate-x-0 lg:w-24 w-16"
        }`}
      >
        <div className="relative h-full overflow-y-auto">
          <button
            onClick={onClose}
            className="md:hidden absolute top-4 left-4 z-50"
          >
            <X className="text-white w-6 h-6" />
          </button>

          <div className="mt-8 mb-6 justify-center">
            <img
              src={LogoSidenavbar}
              alt="Avana Logo"
              className={`transition-all mx-auto ${
                isSidebarVisible ? "w-30 h-12" : "w-10 h-12"
              }`}
            />
          </div>

          <nav className="px-0 space-y-3">
            {navigationItems.map((item, index) => {
              const isActive =
                item.path === "/dashboard"
                  ? ["/dashboard", "/dashboard/SalesVsProducts"].includes(
                      location.pathname
                    )
                  : location.pathname === item.path;

              return (
                <div
                  key={item.path}
                  className="relative"
                  ref={(el) => (navItemsRef.current[index] = el)}
                  onMouseEnter={() => {
                    !isSidebarVisible && setHoveredItem(index);
                  }}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <Link
                    to={item.path}
                    className={`flex items-center text-white px-2 ${
                      isSidebarVisible ? "mx-2" : "mx-2 justify-center"
                    } py-2.5 rounded-lg transition-all duration-200 ${
                      isActive ? "bg-primary shadow-lg" : "bg-transparent"
                    }`}
                  >
                    <item.icon className="w-6 h-6 transition-all text-white" />
                    {isSidebarVisible && (
                      <span className="ml-3 text-base font-medium">
                        {item.label}
                      </span>
                    )}
                  </Link>

                  {/* Tooltip - only shown when sidebar is collapsed and item is hovered */}
                  {!isSidebarVisible && hoveredItem === index && (
                    <div
                      className="fixed whitespace-nowrap bg-secondary text-white px-3 py-1.5 rounded shadow-lg font-medium text-sm z-50 min-w-28"
                      style={{
                        left: `${tooltipPosition.left}px`,
                        top: `${tooltipPosition.top}px`,
                        transform: "translateY(-50%)",
                      }}
                    >
                      {item.label}
                      <div className="absolute left-0 top-1/2 transform -translate-x-1 -translate-y-1/2 border-solid border-transparent border-r-secondary border-l-0 border-t-4 border-b-4 border-r-8"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
};

export default SideNavBar;
